// src/components/homepage/OverheardRows.jsx
//
// Ticket 25 (Overheard on the Shelf). Two rows of example cravings drifting in
// opposite directions under the craving box. Tap one and onPick(text) is called
// with its words; the page hands that to the craving box, which puts them in the
// box as typed text for the reader to edit.
//
// The items come from public.overheard_items through useOverheardItems(), already
// in a fresh order for this visit. This component only lays them out: it splits
// them across the two rows by position and caps how many it draws, so a long
// table does not become an endless strip.
//
// MOTION. Each row is its content twice over, slid by half its width, so the loop
// has no seam. It pauses under the pointer and when anything inside has focus.
// With prefers-reduced-motion the drift stops and each row scrolls by hand.
// The second copy is hidden from screen readers and skipped by Tab.
//
// Renders nothing below two items: one row of one thing is not a drift.

import React from 'react';

// Exported so the admin preview can say how many of the set a reader sees.
export const MAX_ITEMS = 16;

const css = `
.ovh-row { overflow: hidden; }
.ovh-track { display: flex; width: max-content; }
.ovh-row:hover .ovh-track, .ovh-row:focus-within .ovh-track { animation-play-state: paused; }
.ovh-left  .ovh-track { animation: ovhLeft  var(--ovh-seconds, 80s) linear infinite; }
.ovh-right .ovh-track { animation: ovhRight var(--ovh-seconds, 80s) linear infinite; }
@keyframes ovhLeft  { from { transform: translateX(0); }    to { transform: translateX(-50%); } }
@keyframes ovhRight { from { transform: translateX(-50%); } to { transform: translateX(0); } }
.ovh-copy { display: contents; }
.ovh-chip { text-align: left; cursor: pointer; font: inherit; }
.ovh-chip:hover { border-color: var(--brand-rust); }
.ovh-chip:focus-visible { outline: 2px solid var(--brand-rust); outline-offset: 2px; }
@media (prefers-reduced-motion: reduce) {
  .ovh-row { overflow-x: auto; }
  .ovh-left .ovh-track, .ovh-right .ovh-track { animation: none; }
  .ovh-copy { display: none; }
}
`;

function Row({ items, direction, onPick, isMobile }) {
  // ~7s per item keeps a chip readable as it passes; never faster than 40s.
  const seconds = Math.max(40, items.length * 7);
  const chipWidth = isMobile ? 260 : 340;
  const chip = (item, hidden) => (
    <button
      key={`${hidden ? 'copy' : 'main'}-${item.id}`}
      type="button"
      className="ovh-chip"
      tabIndex={hidden ? -1 : 0}
      onClick={() => onPick?.(item.text)}
      style={{
        width: chipWidth, flexShrink: 0, marginRight: 12, padding: '12px 14px',
        background: 'var(--brand-white)', border: '1px solid var(--brand-sand)',
        borderRadius: 'var(--radius-lg)', color: 'var(--neutral-600)',
        fontFamily: 'var(--font-display)', fontStyle: 'italic',
        fontSize: isMobile ? 13 : 14, lineHeight: 1.45, whiteSpace: 'normal',
      }}
    >
      &ldquo;{item.text}&rdquo;
    </button>
  );
  return (
    <div className={`ovh-row ovh-${direction}`} style={{ '--ovh-seconds': `${seconds}s` }}>
      <div className="ovh-track">
        {items.map(i => chip(i, false))}
        <span className="ovh-copy" aria-hidden="true">
          {items.map(i => chip(i, true))}
        </span>
      </div>
    </div>
  );
}

export default function OverheardRows({ items, onPick, isMobile = false }) {
  if (!items || items.length < 2) return null;
  const shown = items.slice(0, MAX_ITEMS);
  const top    = shown.filter((_, i) => i % 2 === 0);
  const bottom = shown.filter((_, i) => i % 2 === 1);
  return (
    <section aria-label="Overheard on the Shelf" style={{ margin: isMobile ? '20px 0 8px' : '28px 0 12px' }}>
      <style>{css}</style>
      <span style={{ display: 'block', fontSize: 'var(--font-size-xxs)', letterSpacing: 2, color: 'var(--brand-rust)', fontWeight: 700, marginBottom: 10 }}>
        OVERHEARD ON THE SHELF
      </span>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Row items={top} direction="left" onPick={onPick} isMobile={isMobile} />
        {bottom.length > 0 && <Row items={bottom} direction="right" onPick={onPick} isMobile={isMobile} />}
      </div>
    </section>
  );
}
